import type { WidgetEventBankClickType, WidgetSuccessPayload, WidgetEventType, WidgetMessageType } from './types/event.interface'
import type { WidgetPostMessageEvent, WidgetPostMessageOnClose, WidgetPostMessageOnSuccess, WidgetPostMessageOnToggleTheme } from './types/post-message.interface'
import type { IframePropsType, WidgetConfigs, WidgetThemeType } from './types/widget.interface'
import { AEROSYNC_EMBEDDED_IFRAME_ID, AEROSYNC_VERSION, AEROSYNC_WIDGET_CONTAINER_ID, AEROSYNC_WIDGET_IFRAME_ID } from './constants'
import { AerosyncUrl } from './types/environment'
import { WidgetMessageEventType } from './types/event.interface'

export class AerosyncWidget {
  private widgetConfigs: WidgetConfigs
  private targetDocument!: ShadowRoot | Document
  private targetElement!: HTMLElement
  private widgetBaseURL!: string
  private widgetStateCode!: string
  private embeddedIframeElement!: HTMLIFrameElement
  private hasMessageListener: boolean = false
  private popupWindow: Window | null = null

  constructor(widgetConfigs: WidgetConfigs) {
    this.widgetConfigs = widgetConfigs
    this.widgetConfigs = {
      ...widgetConfigs,
      onLoad: widgetConfigs.onLoad ?? (() => { }),
      onClose: widgetConfigs.onClose ?? (() => { }),
      onSuccess: widgetConfigs.onSuccess ?? (() => { }),
      onEvent: widgetConfigs.onEvent ?? (() => { }),
      onError: widgetConfigs.onError ?? (() => { }),
    }
    this.addWindowEventListener()
    this.setWidgetBaseURL()
    this.showEmbeddedBanks()
  }

  private setWidgetBaseURL() {
    this.widgetBaseURL = AerosyncUrl[this.widgetConfigs.environment as keyof typeof AerosyncUrl] || AerosyncUrl.production
  }

  private addWindowEventListener() {
    if (!this.hasMessageListener) {
      window.addEventListener('message', this.handlePostMessage)
      this.hasMessageListener = true
    }
  }

  private removeWindowEventListener() {
    if (this.hasMessageListener) {
      window.removeEventListener('message', this.handlePostMessage)
      this.hasMessageListener = false
    }
  }

  private constructUrl(baseUrl: string, queryParams: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(baseUrl)
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(queryParams)) {
      if (value) {
        searchParams.append(key, String(value))
      }
    }
    url.search = searchParams.toString()
    return url.toString()
  }

  public getLaunchUrl(): string {
    return this.constructUrl(this.widgetBaseURL, this.getWidgetQueryParams())
  }

  private getWidgetQueryParams(): Record<string, string | number | boolean | undefined> {
    return {
      token: this.widgetConfigs.token,
      configurationId: this.widgetConfigs.configurationId,
      deeplink: this.widgetConfigs.deeplink,
      handleOAuthManually: this.widgetConfigs.handleOAuthManually,
      manualLinkOnly: this.widgetConfigs.manualLinkOnly,
      aeroPassUserUuid: this.widgetConfigs.aeroPassUserUuid,
      handleMFA: this.widgetConfigs.handleMFA,
      jobId: this.widgetConfigs.jobId,
      connectionId: this.widgetConfigs.connectionId,
      widgetLaunchType: this.widgetConfigs.widgetLaunchType,
      domain: this.widgetConfigs.domain,
      stateCode: this.widgetStateCode,
      defaultTheme: this.widgetConfigs.theme ?? 'light',
      version: AEROSYNC_VERSION,
    }
  }

  public launch(): void {
    const widgetURL = this.constructUrl(this.widgetBaseURL, this.getWidgetQueryParams())

    // handle widget launch type
    if (this.widgetConfigs?.widgetLaunchType === 'systemBrowser') {
      const features = 'width=315,height=635,top=100,left=100,resizable=yes,scrollbars=yes'

      try {
        window.AerosyncWidget.popupWindow = window.open(widgetURL, '_blank', features)

        if (!window.AerosyncWidget.popupWindow) {
          console.warn('Popup was likely blocked by the browser.')
        }

        // Start polling the popup's closed state every 500ms
        const checkInterval = 500 // milliseconds
        const intervalId = setInterval(() => {
          if (window.AerosyncWidget.popupWindow?.closed) {
            clearInterval(intervalId)
            this.widgetConfigs.onClose()
          }
        }, checkInterval)
      }
      catch (error) {
        console.error('Failed to open system browser window:', error)
      }
      return
    }

    let bg = this.widgetConfigs?.style?.bgColor && this.widgetConfigs?.style?.opacity
      ? this.widgetConfigs.style.bgColor + Math.floor(this.widgetConfigs.style.opacity * 255).toString(16)
      : '#000000b2'

    let widgetBox = document.createElement('div')
    this.targetDocument = this.widgetConfigs.targetDocument || document
    widgetBox.setAttribute('id', AEROSYNC_WIDGET_CONTAINER_ID)
    this.targetElement = this.targetDocument.getElementById(this.widgetConfigs.elementId)!
    this.targetElement.innerHTML = ''
    this.targetElement.appendChild(widgetBox)

    const iframeProps: IframePropsType = {
      elementId: AEROSYNC_WIDGET_IFRAME_ID,
      width: this.widgetConfigs?.style?.width ? this.widgetConfigs.style.width : '375px',
      height: this.widgetConfigs?.style?.height ? this.widgetConfigs.style.height : '688px',
      source: widgetURL,
    }
    let iframeElement = this.setIframeWindow(iframeProps)
    iframeElement.setAttribute('width', iframeProps.width)
    iframeElement.setAttribute('height', iframeProps.height)

    let widgetBoxElement = this.targetDocument.getElementById(AEROSYNC_WIDGET_CONTAINER_ID)
    widgetBoxElement!.style.display = 'flex'
    widgetBoxElement!.style.position = 'fixed'
    widgetBoxElement!.style.width = '100%'
    widgetBoxElement!.style.left = '0'
    widgetBoxElement!.style.top = '0'
    widgetBoxElement!.style.backgroundColor = bg
    widgetBoxElement!.style.zIndex = this.widgetConfigs.zIndex || '1'
    widgetBoxElement!.style.height = '100%'
    widgetBoxElement!.style.justifyContent = 'center'
    widgetBoxElement!.style.alignItems = 'center'

    if (window.matchMedia('(max-height: 700px)').matches) {
      iframeElement.setAttribute('height', this.widgetConfigs.style?.height || '95%')
    }

    widgetBoxElement!.innerHTML = ''
    widgetBoxElement!.appendChild(iframeElement)
  }

  private setIframeWindow(props: IframePropsType): HTMLIFrameElement {
    let iframe = document.createElement('iframe')
    iframe.setAttribute('id', props.elementId)
    iframe.setAttribute('border', '0')
    iframe.setAttribute('frame', '0')
    iframe.setAttribute('frameborder', '0')
    iframe.setAttribute('allowTransparency', 'true')
    iframe.setAttribute('src', props.source)
    iframe.setAttribute('marginheight', '0')
    iframe.setAttribute('marginwidth', '0')
    iframe.setAttribute('allow', 'publickey-credentials-get *; publickey-credentials-create *')
    iframe.style.backgroundColor = (this.widgetConfigs?.theme === 'dark')
      ? 'hsl(240, 25%, 5.9%)'
      : 'hsl(0, 0%, 100%)'
    iframe.onload = () => {
      this.widgetConfigs?.embeddedBankView?.onEmbedded() ?? this.widgetConfigs.onLoad()
    }
    iframe.setAttribute('title', this.widgetConfigs.iframeTitle)
    return iframe
  }

  private showEmbeddedBanks(): void {
    if (this.widgetConfigs?.embeddedBankView?.elementId) {
      const widgetQueryParams = {
        token: this.widgetConfigs.token,
        defaultTheme: this.widgetConfigs.theme,
      }
      // container
      let iframeContainerElement = document.createElement('div')
      iframeContainerElement.style.position = 'relative'
      iframeContainerElement.style.width = '100%'
      iframeContainerElement.style.overflow = 'hidden'
      iframeContainerElement.style.height = '100%'

      // iframe
      const widgetURL = this.constructUrl(`${this.widgetBaseURL}/embedded-view`, widgetQueryParams)
      const iframeProps: IframePropsType = {
        elementId: AEROSYNC_EMBEDDED_IFRAME_ID,
        width: this.widgetConfigs?.embeddedBankView?.width || '100%',
        height: this.widgetConfigs?.embeddedBankView?.height || '100%',
        source: widgetURL,
      }
      this.embeddedIframeElement = this.setIframeWindow(iframeProps)
      this.embeddedIframeElement.setAttribute('scrolling', 'no')
      this.embeddedIframeElement.style.overflow = 'hidden'
      this.embeddedIframeElement.style.width = iframeProps.width
      this.embeddedIframeElement.style.height = iframeProps.height
      this.embeddedIframeElement.style.position = 'absolute'
      this.embeddedIframeElement.style.top = '0px'
      this.embeddedIframeElement.style.left = '0px'
      this.embeddedIframeElement.style.bottom = '0px'
      this.embeddedIframeElement.style.right = '0px'

      this.targetDocument = this.widgetConfigs.targetDocument || document
      const embeddedElement = this.targetDocument.getElementById(this.widgetConfigs.embeddedBankView.elementId)!
      embeddedElement.innerHTML = ''
      embeddedElement.appendChild(iframeContainerElement)
      iframeContainerElement.appendChild(this.embeddedIframeElement)
    }
  }

  public destroy(): void {
    this.removeWindowEventListener()
  }

  public toggleTheme(value: WidgetThemeType): void {
    const message: WidgetPostMessageOnToggleTheme = {
      type: 'embeddedView',
      payload: { name: 'onToggleTheme', theme: value },
    }
    this.sendPostMessage(AEROSYNC_EMBEDDED_IFRAME_ID, message)
    this.widgetConfigs.theme = value
  }

  private removeElements(): void {
    if (this.widgetConfigs.widgetLaunchType === 'systemBrowser') {
      return
    }
    const removeElement = (selector: string): void => {
      const element = this.targetDocument.querySelector(selector)
      if (element) {
        element.remove()
      }
    }
    removeElement(`#${AEROSYNC_WIDGET_IFRAME_ID}`)
    removeElement(`#${AEROSYNC_WIDGET_CONTAINER_ID}`)
  }

  private removePopWindow(): void {
    // close the popup window
    try {
      const popup = window.AerosyncWidget?.popupWindow
      if (popup && !popup.closed) {
        popup.close()
      }
      else {
        console.warn('Popup window reference missing or already closed.')
      }
    }
    catch (err) {
      console.warn('Error closing popup window:', err)
    }
  }

  public exit(): void {
    // iframe not available in the system browser type
    if (this.widgetConfigs.widgetLaunchType !== 'systemBrowser') {
      this.removeElements()
    }
    else {
      this.removePopWindow()
    }

    // fire onClose event
    this.widgetConfigs.onClose()
  }

  private handlePostMessage(messageEvent: MessageEvent): void {
    if (
      messageEvent.origin === window.AerosyncWidget.widgetBaseURL
    ) {
      let e: WidgetMessageType
      try {
        e = JSON.parse(messageEvent.data)
        if (e.type) {
          window.AerosyncWidget.handleWidgetEvent(e)
        }
      }
      catch (err) {
        console.warn('Error processing event', err)
      }
    }
  }

  private handleWidgetEvent(t: WidgetMessageType): void {
    switch (t.type) {
      case WidgetMessageEventType.ONSUCCESS:
        this.widgetConfigs.onSuccess(t.payload as WidgetSuccessPayload)
        const message: WidgetPostMessageOnSuccess = {
          type: 'embeddedView',
          payload: { name: 'onSuccess' },
        }
        this.sendPostMessage(AEROSYNC_EMBEDDED_IFRAME_ID, message)
        this.removeElements()
        this.removePopWindow()
        break
      case WidgetMessageEventType.ONEVENT:
        this.widgetConfigs.onEvent(t as WidgetEventType)
        break
      case WidgetMessageEventType.ONCLOSE:
        if (this.widgetConfigs?.embeddedBankView?.elementId
          && this.embeddedIframeElement
        ) {
          const message: WidgetPostMessageOnClose = {
            type: 'embeddedView',
            payload: { name: 'onClose' },
          }
          this.sendPostMessage(AEROSYNC_EMBEDDED_IFRAME_ID, message)
        }
        this.exit()
        break
      case WidgetMessageEventType.ONERROR:
        this.widgetConfigs.onError(t.payload as string)
        break
      case WidgetMessageEventType.ONBANKCLICK:
        this.widgetStateCode = (t.payload as WidgetEventBankClickType).stateCode
        this.launch()
        break
    }
  }

  private sendPostMessage(elementId: string, message: WidgetPostMessageEvent): void {
    const iframe = document.getElementById(elementId) as HTMLIFrameElement
    if (iframe) {
      iframe.contentWindow!.postMessage(
        message,
        window.AerosyncWidget.widgetBaseURL,
      )
    }
  }
}
